import { BillingPeriod, SystemDayOfWeek } from '@benefitsui/app/api/benefits-clients';
import { Defaults } from '@benefitsui/app/shared/defaults/defaults';
import { SymetraDateTime } from '@benefitsui/app/api/benefits-clients';
import { Constants } from '@benefitsui/app/pages/billing-page/billing-page.types';
import { DatePipe } from '@angular/common';
import { parseISO } from 'date-fns';

export class DateTimeUtilities {
  public static MonthDayYearFormat = 'MM/dd/yyyy';
  public static YearMonthDayFormat = 'yyyy-MM-dd';
  public static IsoFormat = 'yyyy-MM-ddTHH:mm:ss.SSSZ';

  public static symetraDateTimeDefault: SymetraDateTime = this.dateToSymetraDateTimeUTC(Defaults.dateDefault);

  static billingPeriodToString(billingPeriod: BillingPeriod | undefined): string {
    let billingPeriodString = Defaults.stringEmpty;
    if (billingPeriod !== undefined) {
      const beginDateString = DateTimeUtilities.symetraDateTimeToString(billingPeriod.from);
      const endDateString = DateTimeUtilities.symetraDateTimeToString(billingPeriod.to);

      billingPeriodString = `${beginDateString} - ${endDateString}`;
    }

    return billingPeriodString;
  }

  static dateToString(date: Date): string {
    const dateString = date.toLocaleDateString(Defaults.usLocale, Defaults.dateFormatOptions);

    return dateString;
  }

  static dateToSymetraDateTimeUTC(date: Date): SymetraDateTime {
    const unixMilliseconds = date.valueOf();
    const month = date.getUTCMonth() + 1; // Months are zero-based in JavaScript
    const day = date.getUTCDate();
    const year = date.getUTCFullYear();
    const dayOfWeekNumber = date.getUTCDay();
    const dayOfWeek = DateTimeUtilities.getDayOfWeek(dayOfWeekNumber);

    const symetraDateTime = {
      unixMilliseconds: unixMilliseconds,
      month: month,
      day: day,
      year: year,
      dayOfWeek: dayOfWeek
    } as SymetraDateTime;

    return symetraDateTime;
  }

  static dateStringToSymetraDateTime(dateString: string): SymetraDateTime {
    if (!dateString || dateString.trim() === '') {
      return DateTimeUtilities.symetraDateTimeDefault;
    }

    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      const symetraDateTime = DateTimeUtilities.dateStringIsoToSymetraDateTime(dateString);

      return symetraDateTime;
    }

    const symetraDateTime = DateTimeUtilities.dateToSymetraDateTimeUTC(date);

    return symetraDateTime;
  }

  static dateStringIsoToSymetraDateTime(dateStringIso: string): SymetraDateTime {
    if (!dateStringIso || dateStringIso.trim() === '') {
      return DateTimeUtilities.symetraDateTimeDefault;
    }

    const date = parseISO(dateStringIso);

    const symetraDateTime = DateTimeUtilities.dateToSymetraDateTimeUTC(date);

    return symetraDateTime;
  }

  static getDayOfWeek(dayOfWeekNumber: number): SystemDayOfWeek {
    switch (dayOfWeekNumber) {
      case 0:
        return SystemDayOfWeek.Sunday;
      case 1:
        return SystemDayOfWeek.Monday;
      case 2:
        return SystemDayOfWeek.Tuesday;
      case 3:
        return SystemDayOfWeek.Wednesday;
      case 4:
        return SystemDayOfWeek.Thursday;
      case 5:
        return SystemDayOfWeek.Friday;
      case 6:
        return SystemDayOfWeek.Saturday;
      default:
        return SystemDayOfWeek.Sunday;
    }
  }

  /* 
  static spanInfoEnhancedToString(spanInfoEnhanced: SpanInfoEnhanced | undefined): string {
    let spanInfoString = Defaults.stringEmpty;

    if (spanInfoEnhanced !== undefined) {
      const beginDateString = DateTimeUtilities.symetraDateTimeEnhancedToString(spanInfoEnhanced.begin);
      const endDateString = DateTimeUtilities.symetraDateTimeEnhancedToString(spanInfoEnhanced.end);

      spanInfoString = `${beginDateString} - ${endDateString}`;
    }

    return spanInfoString;
  }

  static symetraDateTimeEnhancedToString(symetraDateTimeEnhanced: SymetraDateTimeEnhanced | undefined): string {
    const dateString =
      symetraDateTimeEnhanced === undefined
        ? Constants.StringEmpty
        : DateTimeUtilities.unixMillisecondToString(Number(symetraDateTimeEnhanced.unixMilliseconds));

    return dateString;
  }

  static symetraDateTimeToString(symetraDateTime: SymetraDateTime | undefined): string {
    if (symetraDateTime === undefined) {
      return Constants.StringEmpty;
    } else {
      const symetraDateTimeString = `${symetraDateTime}`;
      const date = new Date(symetraDateTimeString);
      const dateString = DateTimeUtilities.dateToString(date);

      return dateString;
    }
  }
 */

  static symetraDateTimeToDate(SymetraDateTime: SymetraDateTime): Date {
    const unixMilliseconds = SymetraDateTime.unixMilliseconds;
    const date = new Date(unixMilliseconds);

    return date;
  }

  static symetraDateTimeToISOString(symetraDateTime: SymetraDateTime | undefined): string {
    if (!symetraDateTime) {
      return Constants.StringEmpty;
    } else {
      const date = DateTimeUtilities.symetraDateTimeToDate(symetraDateTime);
      const dateString = date.toISOString();

      return dateString;
    }
  }

  // :  "2024-01-01T08:00:00Z" format to "01/01/2024" format (by default)
  static symetraDateTimeToString(symetraDateTime: SymetraDateTime | undefined, format?: string): string {
    if (!symetraDateTime) {
      return Constants.StringEmpty;
    } else {
      // :  Check if 'symetraDateTime' is a string
      if (typeof symetraDateTime === 'string' || symetraDateTime instanceof String) {
        const parsedDateTime = DateTimeUtilities.dateStringToSymetraDateTime(symetraDateTime as unknown as string);
        symetraDateTime = parsedDateTime;
      }

      const formatToUse = format || DateTimeUtilities.MonthDayYearFormat;
      const symetraDateTimeString = DateTimeUtilities.symetraDateTimeToISOString(symetraDateTime);
      const date = DateTimeUtilities.symetraDateTimeToDate(symetraDateTime);
      const timeZoneOffset = date.getTimezoneOffset();

      const datePipe = new DatePipe(Constants.LocaleEnglishUs, 'UTC+' + timeZoneOffset.toString());
      const dateString = datePipe.transform(symetraDateTimeString, formatToUse) || Constants.StringEmpty;

      return dateString;
    }
  }

  static unixMillisecondToString(unixMilliseconds: number | undefined): string {
    if (unixMilliseconds === undefined) {
      return Constants.StringEmpty;
    } else {
      const date = new Date(unixMilliseconds);
      const dateString = DateTimeUtilities.dateToString(date);

      return dateString;
    }
  }

  static unixMillisecondToSymetraDateTime(unixMilliseconds: number): SymetraDateTime {
    const date = new Date(unixMilliseconds);
    const symetraDateTime = DateTimeUtilities.dateToSymetraDateTimeUTC(date);

    return symetraDateTime;
  }
}
